import { type UseQueryResult, useQuery } from "@tanstack/react-query";

/**
 * The readiness probe's response. Declared here rather than in `common` because the backend
 * declares its own shape locally in the health controller too: this endpoint has never had a shared
 * contract type, and introducing one is a larger change than the status page needs.
 */
export type AgentHubReadiness = {
  enabled: boolean;
  listening: boolean;
  lockHeld: boolean;
  generation: number;
};

export type HealthState = "up" | "down";
export type ComponentState = "up" | "down" | "disabled";

export type HealthResponse = {
  state: HealthState;
  details: {
    w: ComponentState;
    t?: ComponentState;
    s?: ComponentState;
    agentHub?: AgentHubReadiness;
  };
};

/** What the page shows when the request itself could not be completed. */
export const UNREACHABLE: HealthResponse = {
  state: "down",
  details: { w: "down" },
};

/**
 * ⚠️ Plain `fetch`, not the shared axios instance.
 *
 * Two reasons, both about the page working when the platform is not. `readyz` answers **503 with a
 * meaningful body** when a component is down, and axios rejects on 503, so the body carrying the
 * per-component breakdown would be thrown away exactly when it matters. The shared instance also
 * carries interceptors that act on failures application-wide, which is the wrong behaviour on the
 * one page whose whole job is to render a failure calmly.
 */
export const fetchHealth = async (): Promise<HealthResponse> => {
  try {
    const res = await fetch("/api/v1/health/readyz", {
      headers: { Accept: "application/json" },
    });
    const body = (await res.json()) as HealthResponse;
    if (!body || typeof body.state !== "string" || !body.details) {
      return UNREACHABLE;
    }
    return body;
  } catch {
    // Network failure, or a response that was not JSON: the platform is unreachable from here,
    // which is itself a status worth showing rather than an error to surface.
    return UNREACHABLE;
  }
};

export const useHealth = (): UseQueryResult<HealthResponse, Error> => {
  return useQuery<HealthResponse, Error>({
    queryKey: ["/api/v1/health/readyz"],
    queryFn: fetchHealth,
    retry: false,
    refetchInterval: 30_000,
  });
};
